import { logger } from '../services/logger';
import React, { useState, useEffect, useRef } from 'react';
import { Send, Edit2, Trash2 } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/db';

interface Message {
  id: string;
  user_id: string;
  message: string;
  created_at: string;
  email: string;
  user_metadata: {
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  };
}

interface MarketChatProps {
  marketId: string;
}

export function MarketChat({ marketId }: MarketChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  useEffect(() => {
    // Initial messages fetch
    fetchMessages();

    // Subscribe to new messages
    const subscription = supabase
      .channel('market_chat')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'market_chat',
          filter: `market_id=eq.${marketId}`
        },
        () => {
          // Refetch messages when changes occur
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [marketId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('market_chat_with_users')
        .select('*')
        .eq('market_id', marketId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
      setError(null);
    } catch (err) {
      logger.error('Error fetching messages:', err);
      setError('Failed to load messages');
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !user) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('market_chat')
        .insert({
          market_id: marketId,
          user_id: user.id,
          message: newMessage.trim()
        });

      if (error) throw error;
      setNewMessage('');
      await fetchMessages(); // Fetch updated messages
    } catch (err) {
      logger.error('Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const updateMessage = async (messageId: string) => {
    if (!editText.trim()) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('market_chat')
        .update({ message: editText.trim() })
        .eq('id', messageId);

      if (error) throw error;
      setEditingMessage(null);
      await fetchMessages(); // Fetch updated messages
    } catch (err) {
      logger.error('Error updating message:', err);
      setError('Failed to update message');
    } finally {
      setLoading(false);
    }
  };

  const deleteMessage = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from('market_chat')
        .delete()
        .eq('id', messageId);

      if (error) throw error;
      await fetchMessages(); // Fetch updated messages
    } catch (err) {
      logger.error('Error deleting message:', err);
      setError('Failed to delete message');
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex flex-col h-[500px] bg-white rounded-lg shadow-md">
      {/* Chat Header */}
      <div className="p-4 border-b">
        <h3 className="text-lg font-semibold">Market Chat</h3>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.user_id === user?.id ? 'justify-end' : 'justify-start'
            }`}
          >
            <div
              className={`max-w-[70%] ${
                message.user_id === user?.id
                  ? 'bg-wayja-teal text-white'
                  : 'bg-gray-100'
              } rounded-lg px-4 py-2`}
            >
              {editingMessage === message.id ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="w-full px-2 py-1 rounded border focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setEditingMessage(null)}
                      className="text-sm hover:underline"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => updateMessage(message.id)}
                      className="text-sm font-medium hover:underline"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium">
                      {message.user_metadata?.first_name || message.email}
                    </span>
                    <span className="text-xs opacity-75">
                      {formatTime(message.created_at)}
                    </span>
                  </div>
                  <p>{message.message}</p>
                  {message.user_id === user?.id && (
                    <div className="flex justify-end gap-2 mt-1">
                      <button
                        onClick={() => {
                          setEditingMessage(message.id);
                          setEditText(message.message);
                        }}
                        className="text-xs opacity-75 hover:opacity-100"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => deleteMessage(message.id)}
                        className="text-xs opacity-75 hover:opacity-100"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={sendMessage} className="p-4 border-t">
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !newMessage.trim()}
            className="px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
}